import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft,
  Clock,
  AlertTriangle,
  RefreshCw,
  Trash2,
  HelpCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  CheckCircle,
} from 'lucide-react';
import { IssueAnalysisPanel } from './IssueAnalysisPanel';
import { useTicketChatStore } from '../../store/ticketChatStore';
import { useChatRedirectStore } from '../../store/chatRedirectStore';
import { FlowHelpDialog } from './FlowHelpDialog';
import { issueAnalysisApi } from '../../services/issueAnalysisApi';
import type { AnalysisSummary } from '../../services/issueAnalysisApi';
import { formatRelativeTime as formatTime } from '../../utils/dateFormat';

interface IssueAnalysisPageProps {
  onBack: () => void;
}

const PAGE_SIZE = 10;

export function IssueAnalysisPage({ onBack }: IssueAnalysisPageProps) {
  const [recentAnalyses, setRecentAnalyses] = useState<AnalysisSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<string | undefined>();
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<number | undefined>();
  // Increment to force IssueAnalysisPanel to re-mount when same ticket clicked
  const [selectionKey, setSelectionKey] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  // Set when arriving via Chat's ticket-key redirect link — tells the panel
  // to auto-run intake if this ticket has never been analyzed.
  const [autoIntake, setAutoIntake] = useState(false);

  // Returning from a ticket chat reselects the ticket the analyst left —
  // consumed once, so ordinary visits are unaffected.
  useEffect(() => {
    const r = useTicketChatStore.getState().takeReturnTo();
    if (r) {
      setSelectedTicket(r.ticketKey);
      setSelectedAnalysisId(r.analysisId ?? undefined);
      setSelectionKey((k) => k + 1);
    }
  }, []);

  // Arriving from Chat's ticket-key redirect link — consumed once, isolated
  // from the returnTo mechanism above so it can never affect that flow.
  useEffect(() => {
    const key = useChatRedirectStore.getState().takePendingTicketKey();
    if (key) {
      setSelectedTicket(key);
      setSelectedAnalysisId(undefined);
      setAutoIntake(true);
      setSelectionKey((k) => k + 1);
    }
  }, []);

  // Search + filters
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [warningsOnly, setWarningsOnly] = useState(false);

  // Debounce the search input → actual query used for fetching.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 0 whenever the filter criteria change — otherwise a stale
  // offset can produce an empty page on a narrower result set.
  useEffect(() => {
    setPage(0);
  }, [searchQuery, verifiedOnly, warningsOnly]);

  const loadRecent = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const data = await issueAnalysisApi.listRecent({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        q: searchQuery || undefined,
        verified: verifiedOnly ? true : undefined,
        withWarnings: warningsOnly ? true : undefined,
      });
      setRecentAnalyses(data.analyses);
      setTotal(data.total);
    } catch {
      // Silently fail — history is supplementary
    } finally {
      setLoadingHistory(false);
    }
  }, [page, searchQuery, verifiedOnly, warningsOnly]);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);
  const startIdx = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIdx = Math.min(total, (page + 1) * PAGE_SIZE);
  const filtersActive = searchQuery !== '' || verifiedOnly || warningsOnly;

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setVerifiedOnly(false);
    setWarningsOnly(false);
  };

  const handleSelectTicket = useCallback((ticketKey: string, analysisId?: number) => {
    setSelectedTicket(ticketKey);
    setSelectedAnalysisId(analysisId);
    // A manual pick from the history sidebar is browsing, not "work this
    // ticket" intent — a missing analysis here must still show as an error,
    // never auto-run intake. Only the Chat redirect link opts into that.
    setAutoIntake(false);
    setSelectionKey(k => k + 1);
  }, []);

  const handleAnalysisComplete = useCallback((_ticketKey: string) => {
    // Jump back to page 0 so the new analysis appears at the top; if already
    // on page 0, the page-change effect won't fire, so trigger a manual reload.
    if (page === 0) {
      loadRecent();
    } else {
      setPage(0);
    }
  }, [loadRecent, page]);

  const handleDeleteAnalysis = useCallback(async (e: React.MouseEvent, analysisId: number, ticketKey: string) => {
    e.stopPropagation();
    if (!confirm(`Delete analysis for ${ticketKey}?`)) return;
    try {
      await issueAnalysisApi.deleteById(analysisId);
      if (selectedAnalysisId === analysisId) {
        setSelectedTicket(undefined);
        setSelectedAnalysisId(undefined);
      }
      // Refetch so the current page refills from the next page's items
      // instead of shrinking to 9/10/11-item pages.
      loadRecent();
    } catch (err) {
      alert(`Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [selectedAnalysisId, loadRecent]);


  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Chat
        </button>
        <h1 className="text-lg font-semibold text-gray-800">Issue Analysis</h1>
        <button
          onClick={() => setHelpOpen(true)}
          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
          title="How does Issue Analysis work?"
          aria-label="How does Issue Analysis work?"
        >
          <HelpCircle size={16} />
        </button>
      </header>

      <FlowHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Body: History sidebar + Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left history panel */}
        <aside className="w-64 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              <Clock size={12} />
              Recent Analyses
            </h2>
            <div className="flex items-center gap-1">
              <button
                onClick={loadRecent}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Refresh history"
              >
                <RefreshCw size={12} />
              </button>
            </div>
          </div>

          {/* Search + filters */}
          <div className="px-3 py-2 border-b border-gray-100 space-y-2">
            <div className="relative">
              <Search
                size={12}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search ticket or summary…"
                className="w-full pl-7 pr-7 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X size={12} />
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => setVerifiedOnly((v) => !v)}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border transition-colors ${
                  verifiedOnly
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
                title="Show only verified analyses"
              >
                <CheckCircle size={10} />
                Verified
              </button>
              <button
                onClick={() => setWarningsOnly((w) => !w)}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border transition-colors ${
                  warningsOnly
                    ? 'bg-amber-50 border-amber-300 text-amber-700'
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
                title="Show only analyses with warnings"
              >
                <AlertTriangle size={10} />
                Warnings
              </button>
              {filtersActive && (
                <button
                  onClick={clearFilters}
                  className="text-[10px] text-blue-600 hover:text-blue-800 ml-auto"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto">
            {loadingHistory && recentAnalyses.length === 0 ? (
              <div className="px-3 py-4 text-xs text-gray-400 text-center">Loading...</div>
            ) : recentAnalyses.length === 0 ? (
              <div className="px-3 py-4 text-xs text-gray-400 text-center">
                {filtersActive ? 'No matches' : 'No analyses yet'}
              </div>
            ) : (
              <div className="py-1">
                {recentAnalyses.map((a) => (
                  <button
                    key={`${a.id}-${a.ticket_key}`}
                    onClick={() => handleSelectTicket(a.ticket_key, a.id)}
                    className={`group w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-50 ${
                      selectedTicket === a.ticket_key ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        {a.determined != null && (
                          <span
                            className={`inline-block w-2 h-2 rounded-full ${
                              a.determined ? 'bg-green-500' : 'bg-yellow-400'
                            }`}
                            title={a.determined ? 'RCA determined' : 'RCA not determined'}
                            aria-label={a.determined ? 'RCA determined' : 'RCA not determined'}
                          />
                        )}
                        <span className="text-xs font-semibold text-gray-800 font-mono">{a.ticket_key}</span>
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-400">{formatTime(a.created_at)}</span>
                        <button
                          onClick={(e) => handleDeleteAnalysis(e, a.id, a.ticket_key)}
                          className="p-0.5 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                          title="Delete this analysis"
                        >
                          <Trash2 size={10} />
                        </button>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500 truncate mt-0.5">{a.summary}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-gray-400">{(a.duration_ms / 1000).toFixed(1)}s</span>
                      {a.warning_count > 0 && (
                        <span className="text-[10px] text-amber-500 flex items-center gap-0.5">
                          <AlertTriangle size={9} />
                          {a.warning_count}
                        </span>
                      )}
                      {a.verified_at && (
                        <span className="text-[10px] text-emerald-600 flex items-center gap-0.5">
                          <CheckCircle size={9} />
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </nav>

          {/* Pagination footer */}
          {total > 0 && (
            <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-500">
              <span>
                {startIdx}–{endIdx} of {total}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || loadingHistory}
                  className="p-0.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Previous page"
                >
                  <ChevronLeft size={12} />
                </button>
                <span className="px-1">
                  {page + 1}/{totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1 || loadingHistory}
                  className="p-0.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Next page"
                >
                  <ChevronRight size={12} />
                </button>
              </div>
            </div>
          )}
        </aside>

        {/* Main content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto">
            <IssueAnalysisPanel
              key={selectionKey}
              initialTicketKey={selectedTicket}
              initialAnalysisId={selectedAnalysisId}
              onAnalysisComplete={handleAnalysisComplete}
              autoIntakeIfMissing={autoIntake}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
